import { AlertTriangle, Loader2 } from 'lucide-react'

import type { OcrInProgress } from '@/services/ingestion'

import { ocrAttachmentNote, ocrRecordNote, type OcrNote } from './ocrProgress'

const TONE: Record<OcrNote['tone'], string> = {
  running: 'text-sky-300',
  incomplete: 'text-amber-300',
}

function Icon({ tone }: { tone: OcrNote['tone'] }) {
  return tone === 'running'
    ? <Loader2 size={10} className="shrink-0 animate-spin" aria-hidden />
    : <AlertTriangle size={10} className="shrink-0" aria-hidden />
}

/** The small line under an attachment's badge (or name) while its remaining
 *  pages are read, and after they could not be. Nothing when it is not owed. */
export function OcrAttachmentNote({
  state, sha256, filename,
}: { state: OcrInProgress | null | undefined; sha256?: string | null; filename?: string | null }) {
  const note = ocrAttachmentNote(state, { sha256, filename })
  if (!note) return null
  return (
    <p
      data-testid="ocr-attachment-note"
      role="status"
      className={`mt-0.5 flex items-center gap-1 text-[9px] ${TONE[note.tone]}`}
    >
      <Icon tone={note.tone} />
      <span>{note.text}</span>
    </p>
  )
}

/** The record-level note for the queue detail. Says what the record is waiting
 *  on, and — once the pass is over — what a person must confirm by eye. */
export default function OcrProgressNote({ state }: { state: OcrInProgress | null | undefined }) {
  const note = ocrRecordNote(state)
  if (!note) return null
  const incomplete = note.tone === 'incomplete'
  return (
    <div
      data-testid="ocr-record-note"
      role="status"
      className={`flex items-start gap-2 rounded border px-2.5 py-2 text-[11px] ${
        incomplete
          ? 'border-amber-400/25 bg-amber-400/[0.05] text-amber-200'
          : 'border-sky-400/20 bg-sky-400/[0.04] text-sky-200'
      }`}
    >
      <span className="mt-px"><Icon tone={note.tone} /></span>
      <div className="min-w-0">
        <div className="font-medium">{note.text}</div>
        <div className="text-[10px] text-stone-400">
          {incomplete
            ? 'Values read from the pages that were read are flagged for confirmation; the rest of the scan has not been read.'
            : 'The rest of the scan is being read in the background. Values read from it are flagged for confirmation and arrive on this record when it finishes.'}
        </div>
        {(state?.attachments ?? []).map((a) => (
          <div key={`${a.sha256 ?? ''}${a.filename ?? ''}`} className="text-[10px] text-stone-300">
            {a.filename ?? 'attachment'} — {a.pagesRemaining} page{a.pagesRemaining === 1 ? '' : 's'}{' '}
            {incomplete ? 'not read' : 'remaining'}
          </div>
        ))}
      </div>
    </div>
  )
}
